/**
 * Image Utilities
 *
 * Client-side image downscaling/recompression for uploads so large phone
 * photos aren't stored at full resolution. Mirrors the frontend's
 * resizeImageFile (packages/frontend/src/utils/fileUtils.ts).
 */

/** Long-edge cap in pixels — keeps detail while trimming oversized uploads. */
export const IMAGE_MAX_DIMENSION = 2000;
/** Output JPEG quality (0-1). */
export const IMAGE_JPEG_QUALITY = 0.82;

export interface ResizeImageOptions {
  maxDimension?: number;
  quality?: number;
}

interface DecodedImage {
  image: CanvasImageSource;
  width: number;
  height: number;
  cleanup: () => void;
}

async function decodeImageSource(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
      return { image: bitmap, width: bitmap.width, height: bitmap.height, cleanup: () => bitmap.close() };
    } catch {
      // Fall through to the <img> path.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(`decodeImageSource: failed to decode ${file.name}`));
      el.src = url;
    });
    return {
      image: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      cleanup: () => URL.revokeObjectURL(url),
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

function replaceExtensionWithJpg(name: string): string {
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base}.jpg`;
}

/**
 * Downscale and recompress an image File to a JPEG within budget.
 * Non-image files are returned untouched; failures fall back to the original.
 */
export async function resizeImageFile(file: File, options: ResizeImageOptions = {}): Promise<File> {
  const { maxDimension = IMAGE_MAX_DIMENSION, quality = IMAGE_JPEG_QUALITY } = options;

  if (!file.type.startsWith("image/")) return file;

  let decoded: DecodedImage | null = null;
  try {
    decoded = await decodeImageSource(file);
    const { width: srcW, height: srcH } = decoded;
    if (!srcW || !srcH) return file;

    const scale = Math.min(1, maxDimension / Math.max(srcW, srcH));
    const targetW = Math.max(1, Math.round(srcW * scale));
    const targetH = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(decoded.image, 0, 0, targetW, targetH);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;

    if (scale === 1 && blob.size >= file.size) return file;

    return new File([blob], replaceExtensionWithJpg(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn("[resizeImageFile] processing failed, using original file:", err);
    return file;
  } finally {
    decoded?.cleanup();
  }
}
